<template>
  <div
    ref="inputWrapper"
    class="px-2 py-4.5 w-full flex items-center border border-transparent rounded-lg relative z-10 transition-all duration-200 min-h-14"
    :class="{'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
  >
    <!-- TOOLTIP -->
    <div class="absolute flex items-center h-8 rounded-t-lg -top-8 left-2 right-2 bg-white/90 pl-13" v-if="tooltip.active">
      <div class="absolute w-5 h-3 border-t-2 border-l-2 left-6 top-4 border-silver" />
      <ph-x-circle :size="18" class="absolute text-black cursor-pointer right-2" @click.native="$emit('closeTooltipClick')" />
      <div class="flex items-center gap-2 truncate" v-if="tooltip.params.title && tooltip.params.content">
        <t-text size="md" class="font-semibold" color="black">{{ tooltip.params.title }}</t-text>
        <t-text size="md" class="max-w-full pr-6 truncate" color="black">{{ tooltip.params.content }}</t-text>
      </div>
    </div>
    <!-- TOOLTIP -->

    <span
      ref="label"
      class="top-4.5 text-16 font-normal absolute z-20 leading-5 text-gray transition-all duration-200 pointer-events-none"
      v-if="label && label !== ''"
      :class="{[labelClass]: true, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    >
      {{ label }}
    </span>
    <t-textarea
      ref="input"
      :disabled="disabled"
      v-model="inputVal"
      :value="inputVal ? inputVal : ''"
      @input="input()"
      @keydown="val => keydown(val)"
      @keyup="$emit('keyup', inputVal)"
      @focus="focus()"
      @blur="blur()"
      @change="change"
      :type="inputType"
      :placeholder="placeholder"
      :max-height="250"
      :height="height"
      class="w-full leading-5 text-black outline-none text-16 scrollbar-hidden"
      :class="{'text-gray': disabled, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    />
    <div ref="rightSide" class="absolute cursor-pointer right-2 bottom-4">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import {mapMutations} from 'vuex'
  import {mapGetters} from 'vuex'
  import handleViewport from '@/utils/mixins/handleViewport'
  export default {
    name: 'big-input',
    props: {
      label: String,
      placeholder: {
        type: String,
        required: false
      },
      pattern: {
        type: String,
        default: ''
      },
      labelClass: String,
      inputType: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      autocomplete: String,
      value: String,
      min: [String, Number],
      max: [String, Number],
      step: [String, Number],
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      height: {
        type: Number
      },
      disabled: Boolean,
      preventAutoFocus: {
        type: Boolean,
        default: true
      },
      inputmode: {
        type: String,
        validator: v => ['none', 'numeric', 'tel', 'decimal', 'email', 'url', 'search'].indexOf(v) !== -1
      }
    },
    mixins: [handleViewport],
    data() {
      return {
        inputVal: this.value ? this.value : '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        isValid: true,
        tooltip: {
          active: false,
          params: {
            title: null,
            content: null
          }
        }
      }
    },
    async mounted() {
      await this.componentLoaded()

      this.$refs.input.$el.style.paddingRight = this.$refs.rightSide.clientWidth + 'px'
      if (this.inputType === 'number') {
        this.$refs.input.$el.addEventListener('keypress', e => this.secureNumberInput(e))
      }
      if (this.$refs.input.$el.value !== '') this.focus()
      if (!this.preventAutoFocus && this.$refs.input.$el.value !== '') this.$refs.input.$el.focus()

      setTimeout(() => {
        if (!this.preventAutoFocus && this.$refs.input.$el.value !== '') this.$refs.input.$el.focus()
      }, 100)
    },
    methods: {
      ...mapMutations('layout', ['initLayout']),
      secureNumberInput(e) {
        if ((e.which !== 8 && e.which !== 0 && e.which !== 13 && e.which < 48) || e.which > 57) {
          e.preventDefault()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input && this.$refs.inputWrapper && this.$refs.rightSide && this.$refs.error) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      showTooltip({title, content}) {
        this.tooltip.params.title = title
        this.tooltip.params.content = content
        this.tooltip.active = true
      },
      hideTooltip() {
        this.tooltip.active = false
        this.tooltip.params.title = null
        this.tooltip.params.content = null
      },
      input() {
        if (this.pattern !== '') {
          const regex = new RegExp(this.pattern.replace(/'/g, ''))
          if (!regex.test(this.getValue())) {
            this.isValid = false
          } else {
            this.clearError()
          }
        }
        this.$emit('input', this.getValue())
      },
      keydown(e) {
        if (this.viewport.width >= this.$screen.md) {
          if (e.keyCode === 13) {
            if (e.ctrlKey) {
              this.inputVal += '\n'
            } else {
              e.preventDefault()
              this.$emit('enter')
            }
          }
        }
      },
      focus() {
        if (this.label) {
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['px-2', 'pt-6', 'pb-3'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          this.$refs.input.$el.classList.replace('bottom-4.5', 'bottom-2')
        }

        this.$emit('focus')
      },
      focusInput() {
        this.$refs.input.$el.focus()
      },
      blur() {
        if (this.inputVal.length === 0 && this.label) {
          this.$refs.inputWrapper.classList.replace(['px-2', 'pt-6', 'pb-3'], ['px-2', 'py-4.5'])
          this.$refs.label.classList.replace(
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold'],
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal']
          )
          this.$refs.input.$el.classList.replace('bottom-2', 'bottom-4.5')
        }

        this.$emit('blur')
      },
      change($event) {
        const value = $event.target.value

        if (this.inputType === 'number' && this.min !== null && this.max !== null && this.min >= 0 && this.max >= 0) {
          const notEnough = value < this.min
          const tooMuch = value > this.max
          const err = notEnough ? `The minimum value is ${this.min}!` : tooMuch ? `The maximum value is ${this.max}!` : ''

          if (err) this.$toast.error(err)

          $event.target.value = notEnough ? +this.min : tooMuch ? +this.max : value
        } else {
          $event.target.value = value
        }

        this.$emit('change', $event.target.value)
      },
      getValue() {
        return this.inputVal
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    computed: {
      ...mapGetters(['themeColorRGBA', 'theme'])
    },
    watch: {
      value(newVal) {
        this.inputVal = newVal
        if (!this.preventAutoFocus && (this.inputVal !== null || this.inputVal !== '')) this.$refs.input.$el.focus()
        if (this.$refs.inputWrapper.classList.contains('disabled-input') && (this.inputVal == null || this.inputVal == ''))
          this.$refs.input.$el.blur()
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    beforeDestroy() {
      if (this.inputType === 'number') {
        this.$refs.input.$el.removeEventListener('keypress', e => this.secureNumberInput(e))
      }
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
